import React, { useState, useEffect } from "react";
import axios from "axios";
import apiUrl from "../../../config/config";
import moment from "moment";
import "./ReportsCompleted.css"; // Import CSS for dark theme
import Loading from "./Loading"; // Import Loading component

const Break = () => {
  const [breakData, setBreakData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const dataPerPage = 8;
  const maxPageNumbersToShow = 8;

  const fetchBreakData = async () => {
    try {
      const response = await axios.get(`${apiUrl}api/users/getverifiersLogs`);
      console.log("Full API Response:", response.data);

      if (!response.data.verifiers) {
        console.error("Unexpected API structure. Check API response.");
        return;
      }

      const verifierType = localStorage.getItem("verifiertype");
      const verifierId = localStorage.getItem("verifierid");

      let verifierBreakData = {};

      response.data.verifiers
        .filter((verifier) => {
          if (verifierType === "8") {
            return true; // Show all verifiers
          } else if (verifierType === "2") {
            return verifier._id === verifierId; // Show only the corresponding verifier
          }
          return false;
        })
        .forEach((verifier) => {
          if (verifier.logs) {
            // Sort logs by DateTime in descending order
            verifier.logs.sort(
              (a, b) =>
                moment(b.DateTime, "DD-MM-YYYY HH:mm:ss") -
                moment(a.DateTime, "DD-MM-YYYY HH:mm:ss")
            );

            verifier.logs.forEach((log) => {
              const verifierId = log && log.Verifier_id ? log.Verifier_id : null;
              const breakConsumed =
                log && log.BreaksConsumed ? parseFloat(log.BreaksConsumed) : 0;
              const loginTime = log && log.DateTime ? log.DateTime : "N/A";
              const logoutTime =
                log && log.LoggedoutTime ? log.LoggedoutTime : null;

              if (verifierId) {
                if (!verifierBreakData[verifierId]) {
                  verifierBreakData[verifierId] = {
                    name: verifier.name,
                    totalBreakConsumed: 0,
                    loginTimes: [],
                    logoutTimes: [],
                    dailyWorkedHours: 0,
                    weeklyWorkedHours: 0,
                    monthlyWorkedHours: 0,
                    totalWorkedHours: 0,
                  };
                }

                // Check if the break consumed is today
                if (
                  moment(loginTime, "DD-MM-YYYY HH:mm:ss").isSame(moment(), "day")
                ) {
                  verifierBreakData[verifierId].totalBreakConsumed +=
                    breakConsumed;
                }

                verifierBreakData[verifierId].loginTimes.push(loginTime);
                if (logoutTime) {
                  verifierBreakData[verifierId].logoutTimes.push(logoutTime);
                }

                // Calculate worked hours
                if (logoutTime) {
                  const loginMoment = moment(loginTime, "DD-MM-YYYY HH:mm:ss");
                  const logoutMoment = moment(logoutTime, "DD-MM-YYYY HH:mm:ss");
                  const duration = moment.duration(
                    logoutMoment.diff(loginMoment)
                  );
                  const hoursWorked = duration.asHours();

                  if (loginMoment.isSame(moment(), "day")) {
                    verifierBreakData[verifierId].dailyWorkedHours += hoursWorked;
                  }
                  if (loginMoment.isSameOrAfter(moment().subtract(7, "days"))) {
                    verifierBreakData[verifierId].weeklyWorkedHours +=
                      hoursWorked;
                  }
                  if (loginMoment.isSame(moment(), "month")) {
                    verifierBreakData[verifierId].monthlyWorkedHours +=
                      hoursWorked;
                  }
                  verifierBreakData[verifierId].totalWorkedHours += hoursWorked;
                }
              }
            });

            // Calculate total worked hours for today
            const todayLogs = verifier.logs.filter((log) =>
              moment(log.DateTime, "DD-MM-YYYY HH:mm:ss").isSame(moment(), "day")
            );

            if (todayLogs.length > 0) {
              const lastLog = todayLogs[todayLogs.length - 1];
              const lastLoginTime = lastLog.DateTime;
              const lastLogoutTime =
                lastLog.LoggedoutTime || moment().format("DD-MM-YYYY HH:mm:ss");
              // console.log("verifier name", verifier.name);
              // console.log("Last Login Time:", lastLoginTime);
              // console.log("Logout Time or Now:", lastLogoutTime);

              const loginMoment = moment(lastLoginTime, "DD-MM-YYYY HH:mm:ss");
              const logoutMoment = moment(lastLogoutTime, "DD-MM-YYYY HH:mm:ss");
              const duration = moment.duration(logoutMoment.diff(loginMoment));
              const totalWorkedHoursToday = duration.asHours();

              // console.log("Difference in Hours:", totalWorkedHoursToday);

              if (verifierBreakData[verifier._id]) {
                verifierBreakData[verifier._id].dailyWorkedHours =
                  totalWorkedHoursToday;
              }
            }
          }
        });

      // console.log("Final Aggregated Break Data:", verifierBreakData);

      const breakDataArray = Object.entries(verifierBreakData).map(
        ([verifierId, data], index) => ({
          serialNo: index + 1,
          verifierName: data.name,
          totalBreakConsumed: data.totalBreakConsumed,
          dailyWorkedHours: formatHours(data.dailyWorkedHours),
          weeklyWorkedHours: data.weeklyWorkedHours.toFixed(2),
          monthlyWorkedHours: data.monthlyWorkedHours.toFixed(2),
          totalWorkedHours: data.totalWorkedHours.toFixed(2),
        })
      );

      // Sort by total worked hours
      breakDataArray.sort(
        (a, b) => b.monthlyWorkedHours - a.monthlyWorkedHours
      );

      // console.log("Final Processed Data for Table:", breakDataArray);
      setBreakData(breakDataArray);
      setFilteredData(breakDataArray);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching break data:", error);
      setLoading(false);
    }
  };

  const formatHours = (hours) => {
    const totalSeconds = Math.floor(hours * 3600);
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return `${hrs.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    fetchBreakData();
  }, []);

  // Pagination Logic
  const indexOfLastData = currentPage * dataPerPage;
  const indexOfFirstData = indexOfLastData - dataPerPage;
  const currentData = filteredData.slice(indexOfFirstData, indexOfLastData);
  const totalPages = Math.ceil(filteredData.length / dataPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  return (
    <div className="reports-completed">
      {loading ? (
        <Loading />
      ) : (
        <>
          <table className="reports-table">
            <thead>
              <tr>
                <th>Serial No</th>
                <th>Verifier Name</th>
                <th>Total Break Consumed</th>
                <th>Daily Worked Hours</th>
                <th>Weekly Worked Hours</th>
                <th>Monthly Worked Hours</th>
                <th>Total Worked Hours</th>
              </tr>
            </thead>
            <tbody>
              {currentData.length > 0 ? (
                currentData.map((data, index) => {
                  return (
                    <tr key={index}>
                      <td>{indexOfFirstData + index + 1}</td>
                      <td>{data.verifierName}</td>
                      <td>{data.totalBreakConsumed}</td>
                      <td>{data.dailyWorkedHours}</td>
                      <td>{data.weeklyWorkedHours}</td>
                      <td>{data.monthlyWorkedHours}</td>
                      <td>{data.totalWorkedHours}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>
                    No break data available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="reports-pagination">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map(
              (number) => (
                <button
                  key={number}
                  onClick={() => paginate(number)}
                  className={currentPage === number ? "active" : ""}
                >
                  {number}
                </button>
              )
            )}
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Break;
